import { z } from 'zod'

import type {
  CommonResponseDailyWorksheetResponse,
  CommonResponseWeeklyDailyWorksheetResponse,
  CountRecommendedDailyWorksheetProblemData,
  GetDailyStudentWorksheetScoringByGradeData,
  GetDailyWorksheetFilterSettingData,
  GetDailyWorksheetStatisticsData,
  GetDailyWorksheetStatisticsRankingData,
  GetDailyWorksheetUsedSettingsData,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { type Slice, sliceSchema } from '~/schema/slice'

import {
  type DailyLearningAccessSettings,
  DailyLearningAccessSettingsSchema,
  type DailyLearningFilterSetting,
  DailyLearningFilterSettingSchema,
  type DailyLearningProblemCount,
  DailyLearningProblemCountSchema,
  type DailyLearningRanking,
  DailyLearningRankingListSchema,
  type DailyLearningScoring,
  DailyLearningScoringSchema,
  type DailyLearningStatistic,
  DailyLearningStatisticSchema,
  type DailyWorksheetDay,
  DailyWorksheetDaySchema,
} from './schemas'

const ENTITY = 'DailyLearning'

// 생성 타입과 실제 와이어가 어긋나는 필드:
//   설정의 problemCount·level 등 13개 선택 필드, 통계의 scoreDiff·studentWorksheetId, 채점 현황의
//   studentWorksheetId·score — 생성 타입은 optional(키 생략)로 그리지만 서버는 Kotlin nullable 이라 키를 빼지 않고
//   null 을 싣는다.
//   순위의 student — 실제는 중첩 클래스 DailyWorksheetStatisticsRankingResponse.Student(studentId·name·schoolType·
//   grade)인데, springdoc 이 동명의 다른 클래스(Student)와 병합해 전혀 다른 필드로 그린다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 응답 클래스(schemas.ts 머리 주석)로 확인.
// 스키마가 실제 와이어를 검증하므로 입력을 이 한 자리에서 unknown 으로 넘긴다.
const asWire = (
  raw:
    | GetDailyWorksheetFilterSettingData
    | GetDailyWorksheetStatisticsData
    | GetDailyWorksheetStatisticsRankingData
    | GetDailyStudentWorksheetScoringByGradeData,
): unknown => raw

export const adaptDailyLearningAccessSettings = (
  raw: GetDailyWorksheetUsedSettingsData,
): DailyLearningAccessSettings => parseApiResponse(DailyLearningAccessSettingsSchema, raw, ENTITY)

export const adaptDailyLearningFilterSetting = (
  raw: GetDailyWorksheetFilterSettingData,
): DailyLearningFilterSetting =>
  parseApiResponse(DailyLearningFilterSettingSchema, asWire(raw), ENTITY)

export const adaptDailyLearningProblemCount = (
  raw: CountRecommendedDailyWorksheetProblemData,
): DailyLearningProblemCount => parseApiResponse(DailyLearningProblemCountSchema, raw, ENTITY)

export const adaptDailyLearningStatistics = (
  raw: GetDailyWorksheetStatisticsData,
): Slice<DailyLearningStatistic> =>
  parseApiResponse(sliceSchema(DailyLearningStatisticSchema), asWire(raw), ENTITY)

export const adaptDailyLearningRankings = (
  raw: GetDailyWorksheetStatisticsRankingData,
): DailyLearningRanking[] =>
  parseApiResponse(DailyLearningRankingListSchema, asWire(raw), ENTITY)?.rankings ?? []

export const adaptDailyLearningScorings = (
  raw: GetDailyStudentWorksheetScoringByGradeData,
): Slice<DailyLearningScoring> =>
  parseApiResponse(sliceSchema(DailyLearningScoringSchema), asWire(raw), ENTITY)

export const adaptDailyWorksheetDays = (
  raw: CommonResponseWeeklyDailyWorksheetResponse['data'],
): DailyWorksheetDay[] =>
  parseApiResponse(z.object({ dailyWorksheets: z.array(DailyWorksheetDaySchema) }), raw, ENTITY)
    ?.dailyWorksheets ?? []

/** 만들지 못하면 서버가 studentWorksheetId 를 null 로 낸다 */
export const adaptCreatedDailyWorksheetId = (
  raw: CommonResponseDailyWorksheetResponse['data'],
): DailyWorksheetDay['studentWorksheetId'] =>
  parseApiResponse(
    z.object({ studentWorksheetId: DailyWorksheetDaySchema.shape.studentWorksheetId }),
    raw,
    ENTITY,
  )?.studentWorksheetId ?? null
